import "./App.css";
import {useCallback, useEffect, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import PrivateRoutes from "./PrivateRoutes";
import {ThemeProvider} from "@mui/material";
import {SnackbarProvider} from "notistack";
import {ConfigProvider, theme as themeAntd} from "antd";
import getTheme from "./ui/theme";
import dayjs from "dayjs";
import tz from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import {RootState} from "./store/store";
import {oidcConfig} from "./config";
import Toast from "./service/Toast";

import {AuthProvider} from "react-oidc-context";
import {requirePrivacyApproval, resetPrivacyApproval} from "./store/privacy/privacy.store";
import {eventBus} from "./events/eventBus";
import {useNavigate} from "react-router";
import {pathWithoutOidcParams} from "./service/oidcCallbackUrl";

import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import {ReactQueryDevtools} from "@tanstack/react-query-devtools";
import {useTranslation} from "react-i18next";
import {LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {languageSettings} from "./i18n/languages";

const queryClient = new QueryClient();

dayjs.extend(tz);
dayjs.extend(utc);

// antd 6 éclaircit les ombres en thème sombre (blanc à 1-2 % d'opacité), ce qui efface celle des
// panneaux du DatePicker. On garde en sombre les ombres noires du thème clair, comme en antd 5.
const {boxShadow, boxShadowSecondary} = themeAntd.getDesignToken();

function App() {
  const {defaultAlgorithm, darkAlgorithm} = themeAntd;
  const [mode, setMode] = useState("light");
  const darkMode = useSelector((state: RootState) => state.theme.darkMode);
  const dispatch = useDispatch();
  const {i18n} = useTranslation();
  const language = languageSettings(i18n.resolvedLanguage);
  const navigate = useNavigate();

  // Au retour de Keycloak, on retire les paramètres OIDC de l'URL par une navigation du routeur, et
  // non par `window.location.replace` : celui-ci rechargeait toute la page (document et bundle) juste
  // après la connexion, et une coupure réseau pendant ce second chargement laissait un écran blanc.
  // `history.replaceState` ne convient pas non plus : le routeur ne le voit pas, sa `location`
  // garderait `code` et `state`, et une page qui réécrit ses paramètres les remettrait dans l'URL.
  const onSigninCallback = useCallback(
    () => navigate(pathWithoutOidcParams(window.location.href), {replace: true}),
    [navigate],
  );

  useMemo(() => {
    if (darkMode) {
      setMode("dark");
    } else {
      setMode("light");
    }
  }, [darkMode]);

  useEffect(() => {
    const privacyApprovalRequiredHandler = () => {
      dispatch(requirePrivacyApproval());
    };
    const privacyApprovalNotRequiredHandler = () => {
      dispatch(resetPrivacyApproval());
    };
    eventBus.on("privacyApprovalRequired", privacyApprovalRequiredHandler);
    eventBus.on("privacyApprovalNotRequired", privacyApprovalNotRequiredHandler);
    return () => {
      eventBus.off("privacyApprovalRequired", privacyApprovalRequiredHandler);
      eventBus.off("privacyApprovalNotRequired", privacyApprovalNotRequiredHandler);
    };
  }, [dispatch]);

  const theme = useMemo(() => getTheme(mode, language.mui), [mode, language]);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <ConfigProvider
          locale={language.antd}
          theme={{
            algorithm: darkMode ? darkAlgorithm : defaultAlgorithm,
            token: darkMode ? {boxShadow, boxShadowSecondary} : undefined,
          }}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={language.dayjs}>
            <SnackbarProvider maxSnack={3}>
              <AuthProvider {...oidcConfig} onSigninCallback={onSigninCallback}>
                <PrivateRoutes />
                <Toast />
              </AuthProvider>
            </SnackbarProvider>
          </LocalizationProvider>
        </ConfigProvider>
      </ThemeProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;
